import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type LocationModel from 'garaje/models/location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import type { BinaryDownloadModalOption } from 'garaje/utils/binary-download-options';
import {
  OPERATING_SYSTEM_OPTIONS,
  PLATFORM_ARCHITECTURE_OPTIONS,
  VALID_COMBINATIONS,
} from 'garaje/utils/binary-download-options';
import urlBuilder from 'garaje/utils/url-builder';

interface WorkplaceSettingsBinaryDownloadDownloadModalArgs {
  location: LocationModel;
}

/**
 * @param {Class<Location>} location Location
 * @param {Boolean} [disabled] If true, open button is disabled
 */
export default class WorkplaceSettingsBinaryDownloadDownloadModal extends Component<WorkplaceSettingsBinaryDownloadDownloadModalArgs> {
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked isSettingsPanelOpen = false;
  @tracked isOperatingSystemPresent = false;
  @tracked isPlatformArchitecturePresent = false;
  @tracked isDownloadingPublicKey = false;
  @tracked isDownloadingHashSignature = false;
  @tracked isDownloadingClient = false;
  @tracked hashText = 'Please choose an OS and architecture option above';
  @tracked showLoadingSpinner = false;
  @tracked selectedOperatingSystem: BinaryDownloadModalOption | null = null;
  @tracked selectedPlatformArchitecture: BinaryDownloadModalOption | null = null;

  get availableOperatingSystems(): BinaryDownloadModalOption[] {
    const selectedArch = this.selectedPlatformArchitecture?.value;
    if (!selectedArch) {
      return OPERATING_SYSTEM_OPTIONS;
    }

    const validOsValues = VALID_COMBINATIONS.filter(([_os, arch]) => arch === selectedArch).map(([os, _arch]) => os);

    return OPERATING_SYSTEM_OPTIONS.filter((option) => validOsValues.includes(option.value));
  }

  get availablePlatformArchitectures(): BinaryDownloadModalOption[] {
    const selectedOs = this.selectedOperatingSystem?.value;
    if (!selectedOs) {
      return PLATFORM_ARCHITECTURE_OPTIONS;
    }

    const validArchValues = VALID_COMBINATIONS.filter(([os, _arch]) => os === selectedOs).map(([_os, arch]) => arch);

    return PLATFORM_ARCHITECTURE_OPTIONS.filter((option) => validArchValues.includes(option.value));
  }

  get downloadClientUrl(): string {
    const os = this.selectedOperatingSystem?.value;
    const arch = this.selectedPlatformArchitecture?.value;
    if (!os || !arch) {
      return '';
    }
    return urlBuilder.v3.diplomatClients.download(os, arch);
  }

  get downloadPublicKeyUrl(): string {
    return urlBuilder.v3.diplomatClients.publicKey();
  }

  get downloadHashSignatureUrl(): string {
    const os = this.selectedOperatingSystem?.value;
    const arch = this.selectedPlatformArchitecture?.value;
    if (!os || !arch) {
      return '';
    }
    return urlBuilder.v3.diplomatClients.hashSignature(os, arch);
  }

  /**
   * Sets the operatingSystem value in the location changeset
   *
   * @param {BinaryDownloadModalOption} operatingSystem
   */
  @action
  async setOperatingSystem(operatingSystem: BinaryDownloadModalOption): Promise<void> {
    this.selectedOperatingSystem = operatingSystem;
    this.isOperatingSystemPresent = true;

    // Clear architecture if it's no longer valid for the new OS
    if (this.selectedPlatformArchitecture) {
      const isValid = VALID_COMBINATIONS.some(
        ([os, arch]) => os === operatingSystem.value && arch === this.selectedPlatformArchitecture?.value,
      );
      if (!isValid) {
        this.selectedPlatformArchitecture = null;
        this.isPlatformArchitecturePresent = false;
        this.hashText = 'Please choose an OS and architecture option above';
        return;
      }
    }

    await this.generateHash();
  }

  /**
   * Sets the platformArchitecture value in the location changeset
   *
   * @param {BinaryDownloadModalOption} platformArchitecture
   */
  @action
  async setPlatformArchitecture(platformArchitecture: BinaryDownloadModalOption): Promise<void> {
    this.selectedPlatformArchitecture = platformArchitecture;
    this.isPlatformArchitecturePresent = true;

    // Clear OS if it's no longer valid for the new architecture
    if (this.selectedOperatingSystem) {
      const isValid = VALID_COMBINATIONS.some(
        ([os, arch]) => os === this.selectedOperatingSystem?.value && arch === platformArchitecture.value,
      );
      if (!isValid) {
        this.selectedOperatingSystem = null;
        this.isOperatingSystemPresent = false;
        this.hashText = 'Please choose an OS and architecture option above';
        return;
      }
    }

    await this.generateHash();
  }

  /**
   * Shows settings panel
   */
  @action
  showSettings(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_BINARY_DOWNLOAD_ENABLE_BUTTON_CLICKED');
    this.isSettingsPanelOpen = true;
  }

  /**
   * Hides settings panel
   */
  @action
  hideSettings(): void {
    this.isSettingsPanelOpen = false;
    this.showLoadingSpinner = false;
  }

  @action
  async generateHash(): Promise<void> {
    const selectedOs = this.selectedOperatingSystem?.value;
    const selectedArch = this.selectedPlatformArchitecture?.value;
    if (!selectedOs || !selectedArch) {
      return;
    }
    const url = urlBuilder.v3.diplomatClients.hash(selectedOs, selectedArch);
    this.hashText = 'Fetching hash...';
    this.showLoadingSpinner = true;
    try {
      const response = await fetch(url);
      const text = await response.text();
      this.hashText = text;
      this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_HASH_GENERATED');
    } catch {
      this.hashText = 'Failed to fetch hash, please try again';
    }
    this.showLoadingSpinner = false;
  }

  @action
  disableDownloadPublicKeyButton(): void {
    this.isDownloadingPublicKey = true;

    setTimeout(() => {
      this.isDownloadingPublicKey = false;
    }, 2000);
  }

  @action
  disableDownloadHashSignatureButton(): void {
    this.isDownloadingHashSignature = true;

    setTimeout(() => {
      this.isDownloadingHashSignature = false;
    }, 2000);
  }

  @action
  disableDownloadClientButton(): void {
    this.isDownloadingClient = true;

    setTimeout(() => {
      this.isDownloadingClient = false;
    }, 2000);
  }
}
